import React, { useState, useEffect } from "react"; // Completed Grid v2 migration
import { useSelector } from "react-redux";
import {
  getMyProducts,
  IProductAndName,
} from "../../api/Account/myProductsApi";
import { ISession, ILoggedUser } from "../../types/Auth";
//import { ProductInfo } from "@softion/b2c-service";
import { RootState } from "../../redux/reducers/rootReducer";
import {
  Container,
  Chip,
  Box,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { getWikiListing } from "../../api/wikiApi";
import { makeStyles } from "@mui/styles";
// eslint-disable-next-line import/named
import { Theme } from "@mui/material/styles";
import { docsResources } from "./.resources";
import { useResources } from "../../hooks/useResources";
import { toast, ToastContainer } from "react-toastify";
import { IUserV0, getUsersList } from "../../api/Account/usersApi";
import { useNavigate } from "react-router-dom";
import PersonIcon from "@mui/icons-material/Person";
import { ThemeOptions } from "../../types/Enums";
import MyDocumentsGrid, { IMyDocumentRow } from "./MyDocumentsGrid";
import PageTitle from "../_shared/PageTitle";
import PageToolbar from "../_shared/PageToolbar";

export interface IMyDocumentsPageProps {
  themeName: ThemeOptions;
}

const MyDocumentsPage: React.FC<IMyDocumentsPageProps> = () => {
  const navigate = useNavigate();

  const session: ISession = useSelector((state: RootState) => {
    return state.auth;
  }); //REDUX

  const loggedUser: ILoggedUser = useSelector((state: RootState) => {
    return state.userInfo;
  });

  // Language sync is handled globally in App.tsx
  const res = useResources(docsResources);
  
  const language: string = useSelector((state: RootState) => {
    return state.language;
  });

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [, setProducts] = useState<IProductAndName[]>([]);
  const [manuals, setManuals] = useState<IMyDocumentRow[]>([]);
  const [impersonatedUser, setImpersonatedUser] = useState<
    string | null | undefined
  >(null);
  const [, setUsers] = useState<IUserV0[]>([]);

  const showError = (err: string | undefined) => {
    if (err) {
      toast.error(err, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  const goToPage = (productId: string | undefined) => {
    if (productId) {
      navigate(`/manuals/${productId}`);
    }
  };

  const refreshUsersAsync = async () => {
    if (loggedUser.isAdmin) {
      await getUsersList(
        (response) => {
          const orderedUsers: IUserV0[] = response.sort((a, b) => {
            if (a.name < b.name) return -1;
            if (a.name > b.name) return 1;
            return 0;
          });
          setUsers([...orderedUsers]);
        },
        (err) => showError(err)
      );
    }
  };

  const getWiki = async (products: IProductAndName[]) => {
    let scopes: string[] = ["Public"];
    if (loggedUser.isAdmin) scopes = [...scopes, "Admin"];
    else if (loggedUser.isDistributor) scopes = [...scopes, "Distributor"];
    else if (loggedUser.isSupport) scopes = [...scopes, "Support"];
    else if (loggedUser.isCustomer) scopes = [...scopes, "Customer"];
    await getWikiListing(
      products.map((x) => x.productId || ""),
      scopes,
      "Manuals",
      language,
      (wikis) => {
        setManuals(
          wikis.map((x) => ({
            productId: x.productId,
            productName:
              products.find((p) => p.productId === x.productId)?.productName ||
              "",
            documentName: x.displayName || "",
            onOpen: (p) => goToPage(p || ""),
          }))
        );
        setIsLoading(false);
      },
      (error) => {
        showError(error);
        setIsLoading(false);
      }
    );
  };

  const getProductsAsync = async (user: string | undefined) => {
    const userId = user || session?.objectId;
    if (userId) {
      setIsLoading(true);
      setImpersonatedUser(null);
      await getMyProducts(
        userId,
        undefined,
        (response) => {
          const myProducts: IProductAndName[] = response.products.map((x) => ({
            productId: x.productId || "",
            productName: x.productName,
          }));
          setProducts(myProducts);
          if (userId !== null) {
            setImpersonatedUser(response.userName);
          }
          getWiki(myProducts);
        },
        (error) => {
          showError(error);
          setProducts([]);
          setManuals([]);
          setIsLoading(false);
        }
      );
    } else {
      setProducts([]);
      setManuals([]);
    }
  };


  useEffect(() => {
    if (loggedUser?.userId) {
      if (loggedUser.isAdmin) {
        refreshUsersAsync();
      }
      getProductsAsync(loggedUser.userId);
    }
  }, [loggedUser]);

  const [,] = useState<string | undefined>();

  const themedStyles = makeStyles((theme: Theme) => ({
    input: {
      marginLeft: theme.spacing(1),
      width: 320,
      flex: 1,
    },
    chip: {
      margin: "10px 0px 10px 0px",
    },
    topContainer: { paddingTop: "24px" },

    gridContainer: {
      padding: "32px",
      borderRadius: "26px",
    },

    [theme.breakpoints.down("sm")]: {
      gridContainer: {
        padding: "16px",
        borderRadius: "8px",
      },
    },
    searchButton: {
      padding: "2px",
      margin: "0px",
      width: "32px",
      height: "32px",
    },
    clearButton: {
      padding: "0px",
      margin: "0px",
    },
    breadCrums: {
      padding: "0 0 12px 0",
    },
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      color: "#fff",
    },
  }));
  const styles = themedStyles();

  const MyDocumentsToolbar = () => {
    return (
      <Box>
        {loggedUser?.isAdmin && impersonatedUser ? (
          <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
            <Chip
              label={impersonatedUser}
              icon={<PersonIcon />}
              color="primary"
              className={styles.chip}
            />
          </Box>
        ) : null}
      </Box>
    );
  };

  return session.loggedIn ? (
    <Container className={styles.topContainer}>
      <PageTitle title={res.documentation} />
      <PageToolbar enabled={isLoading == false}>
        <MyDocumentsToolbar />
      </PageToolbar>

      <div>
        <Backdrop className={styles.backdrop} open={isLoading}>
          <CircularProgress color="inherit" />
        </Backdrop>

        <ToastContainer />

        {!isLoading && (
          <Box className={styles.gridContainer}>
            <MyDocumentsGrid documents={manuals} />
          </Box>
        )}
      </div>
    </Container>
  ) : (
    <div>Your session has expired</div>
  );
};

export default MyDocumentsPage;
